/**
 * The one bounded dispatcher for a Panel action over a collection (QR
 * print-all, grouped inbox receipts, Menu translation proposals).
 *
 * - At most `concurrency` member calls are in flight; a slot that settles takes
 *   the next member at once, in collection order.
 * - Membership is complete: the result holds exactly one outcome per member,
 *   in member order, so a caller can tell a member that settled from one that
 *   failed (threw) and from one that was never sent.
 * - Nothing new is sent once the scope retires (`retired()` true or `signal`
 *   aborted) or once the caller's `stopAfter` policy says so. Calls already in
 *   flight still settle and keep their outcome; the dispatcher never cancels a
 *   mutation that may have reached the server.
 * - The dispatcher creates no identity: a caller that retries a member resends
 *   the exact idempotency key it retained for it. A member that was never sent
 *   or whose outcome is uncertain keeps that key for the next attempt.
 * - A thrown member call is an outcome (`failed`), never a rejection of the
 *   whole dispatch: the caller decides, once, after every in-flight call has
 *   settled, what the partial outcome means.
 */
const BOUNDED_DISPATCH_CONCURRENCY = 4;

type DispatchedOutcome<Member, Value> =
  | Readonly<{ kind: 'settled'; member: Member; index: number; value: Value }>
  | Readonly<{ kind: 'failed'; member: Member; index: number; error: unknown }>;
type DispatchOutcome<Member, Value> =
  | DispatchedOutcome<Member, Value>
  | Readonly<{ kind: 'not_dispatched'; member: Member; index: number }>;

export interface BoundedDispatchOptions<Member, Value> {
  /** Maximum calls in flight; defaults to `BOUNDED_DISPATCH_CONCURRENCY`. */
  readonly concurrency?: number;
  /** Aborting it stops new dispatch, exactly like `retired()`. */
  readonly signal?: AbortSignal;
  /** True once the owning scope retired: no member is sent after that. */
  readonly retired?: () => boolean;
  /** The caller's failure policy: true stops new dispatch after this outcome. */
  readonly stopAfter?: (outcome: DispatchedOutcome<Member, Value>) => boolean;
}

export type BoundedDispatchResult<Member, Value> = Readonly<{
  /** One outcome per member, in member order. */
  outcomes: readonly DispatchOutcome<Member, Value>[];
  /** Why dispatch stopped before every member was sent, or `null` when every member was sent. */
  stopped: 'retired' | 'policy' | null;
}>;

export async function boundedDispatch<Member, Value>(
  members: readonly Member[],
  call: (member: Member, index: number) => Promise<Value>,
  options: BoundedDispatchOptions<Member, Value> = {},
): Promise<BoundedDispatchResult<Member, Value>> {
  const limit = Math.max(1, Math.floor(options.concurrency ?? BOUNDED_DISPATCH_CONCURRENCY));
  const outcomes: DispatchOutcome<Member, Value>[] = members.map((member, index) => ({ kind: 'not_dispatched', member, index }));
  const retired = () => options.signal?.aborted === true || options.retired?.() === true;
  let cursor = 0;
  let stopped: 'retired' | 'policy' | null = null;
  const worker = async () => {
    while (stopped === null && cursor < members.length) {
      if (retired()) { stopped = 'retired'; return; }
      const index = cursor++;
      const member = members[index]!;
      let outcome: DispatchedOutcome<Member, Value>;
      try {
        outcome = { kind: 'settled', member, index, value: await call(member, index) };
      } catch (error) {
        outcome = { kind: 'failed', member, index, error };
      }
      outcomes[index] = outcome;
      if (stopped === null && options.stopAfter?.(outcome) === true) stopped = 'policy';
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, members.length) }, worker));
  return { outcomes, stopped };
}
